import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider } from './hooks/ThemeContext';

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    sendDefaultPii: false,
    enabled: !!process.env.REACT_APP_SENTRY_DSN,
});

(function patchFetch() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.__rezifyFetchPatched) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  const toUrl = (input) => {
    if (input instanceof Request) {
      return new URL(input.url, window.location.origin);
    }

    if (typeof input === 'string') {
      return new URL(input, window.location.origin);
    }

    return null;
  };

  const isApiRequest = (url) => Boolean(url?.pathname?.startsWith('/api'));

  window.__rezifyFetchPatched = true;

  window.fetch = async function (input, init = {}) {
    const requestInit = { ...init };

    if (requestInit.credentials === undefined) {
      requestInit.credentials = 'include';
    }

    const requestUrl = toUrl(input);
    const method = (requestInit.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();

    try {
      const response = await originalFetch(input, requestInit);

      if (isApiRequest(requestUrl) && response.status >= 500) {
        Sentry.captureException(new Error(`API request failed with status ${response.status}`), {
          tags: {
            'api.method': method,
            'api.status': String(response.status),
          },
          extra: {
            url: requestUrl.toString(),
            pathname: requestUrl.pathname,
          },
        });
      }

      return response;
    } catch (error) {
      if (isApiRequest(requestUrl) && error?.name !== 'AbortError') {
        Sentry.captureException(error, {
          tags: {
            'api.method': method,
            'api.status': 'network_error',
          },
          extra: {
            url: requestUrl?.toString(),
            pathname: requestUrl?.pathname,
          },
        });
      }

      throw error;
    }
  };
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <BrowserRouter>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
